/*
 * Single source of truth for the application/job lifecycle.
 *
 * WHY THIS EXISTS: the bug where a rescinded/closed application "just
 * disappeared" happened because a status (e.g. 'Job Filled') belonged to NO
 * tab in the sitter UI, so it rendered nowhere. That's invisible to ordinary
 * unit tests. By centralizing the rules here and asserting invariants in
 * applicationLifecycle.test.js, any NEW status that isn't assigned to a tab
 * fails the test suite instead of silently vanishing in production.
 *
 * If you add a new application status, you MUST add it to ALL_STATUSES and to
 * SITTER_TAB_FOR_STATUS — the test enforces this.
 */

// Every application status the system can persist.
export const ALL_STATUSES = [
  'Pending',
  'Awaiting Sitter Confirmation',
  'Active',
  'Completed',
  'Paid',
  'Family Denied',
  'Sitter Denied',
  'Job Filled',
  'Listing Closed',
  'Parent Cancelled',
  'Sitter Cancelled',
  'Refunded',
];

// Statuses where a sitter is actively engaged with the job (offer out, booked,
// worked, or paid). Used to decide "who is the job's sitter" and whether a job
// counts as filled/active.
export const ENGAGED_STATUSES = [
  'Awaiting Sitter Confirmation',
  'Active',
  'Completed',
  'Paid',
];

// Terminal states that end an application without ongoing engagement. These all
// belong to the sitter's "Closed" tab so they never disappear silently.
export const CLOSED_STATUSES = [
  'Family Denied',
  'Sitter Denied',
  'Job Filled',
  'Listing Closed',
  'Parent Cancelled',
  'Sitter Cancelled',
  'Refunded',
];

// Which SINGLE sitter "Your Jobs" tab each status shows under. Every status
// maps to exactly one tab — that's the invariant the test guards.
export const SITTER_TAB_FOR_STATUS = {
  'Pending': 'Pending Applications',
  'Awaiting Sitter Confirmation': 'Job Offers',
  'Active': 'Active Jobs',
  'Completed': 'Pending Hours',
  'Paid': 'Job History',
  'Family Denied': 'Closed',
  'Sitter Denied': 'Closed',
  'Job Filled': 'Closed',
  'Listing Closed': 'Closed',
  'Parent Cancelled': 'Closed',
  'Sitter Cancelled': 'Closed',
  'Refunded': 'Closed',
};

export const SITTER_TABS = [
  'Pending Applications',
  'Job Offers',
  'Active Jobs',
  'Pending Hours',
  'Job History',
  'Closed',
];

export function isEngaged(status) {
  return ENGAGED_STATUSES.includes(status);
}

export function isClosed(status) {
  return CLOSED_STATUSES.includes(status);
}

export function sitterTabForStatus(status) {
  return SITTER_TAB_FOR_STATUS[status] || null;
}

// Is a job (by its endDate string 'YYYY-MM-DD') already past, relative to `now`?
export function isDatePast(endDateStr, now = new Date()) {
  if (!endDateStr) return false;
  const end = new Date(`${endDateStr}T23:59:59`);
  if (Number.isNaN(end.getTime())) return false;
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  return end < todayStart;
}

/**
 * Does this application belong in the given sitter tab right now?
 * Encodes BOTH the status→tab mapping and the date rule (Pending Applications
 * hides jobs whose date already passed — those move to Closed via the backend
 * sweep).
 */
export function belongsInSitterTab(application, tab, now = new Date()) {
  if (!application) return false;
  const mapped = sitterTabForStatus(application.status);
  if (mapped !== tab) return false;
  if (tab === 'Pending Applications' && isDatePast(application.jobEndDate, now)) {
    return false; // date passed — no longer pending; shows in Closed after sweep
  }
  return true;
}
